/**
 * SINGLE SOURCE OF TRUTH for the inbound payment webhook contract.
 *
 * These are the exact values an operator pastes into the CRM (GoHighLevel)
 * workflow. They are also what the payment-webhook edge function enforces, so
 * if this file and the function ever disagree the function wins — every value
 * here is mirrored from it and is verified live by the admin panel's
 * "config" probe (Admin > Webhook & recovery).
 *
 * Nothing secret lives here. The shared secret is stored only as the
 * GHL_PAYMENT_WEBHOOK_SECRET edge-function environment variable; the UI can
 * confirm it is set and show a short fingerprint, never the value.
 */

/** Project API origin. Same host the browser already talks to. */
export const SUPABASE_ORIGIN = 'https://knvvdlqlzmcyeaszdshe.databasepad.com';

/** THE production endpoint. POST here on a successful payment. */
export const PAYMENT_WEBHOOK_URL = `${SUPABASE_ORIGIN}/functions/v1/payment-webhook`;

export const WEBHOOK_METHOD = 'POST';
export const WEBHOOK_CONTENT_TYPE = 'application/json';

/** Payload authentication is a single custom header carrying a shared secret. */
export const WEBHOOK_HEADER_NAME = 'x-webhook-secret';
export const WEBHOOK_SECRET_ENV = 'GHL_PAYMENT_WEBHOOK_SECRET';

/**
 * PLATFORM GATEWAY TOKEN — publishable, and NOT optional.
 *
 * Every request to /functions/v1/* passes the hosting platform's gateway
 * BEFORE the payment-webhook code runs. A delivery that carries only
 * x-webhook-secret is refused there with:
 *
 *   401 {"code":"DP003_INVALID_TOKEN","message":"Authorization token is required"}
 *
 * Nothing is logged, no security event is written and the CRM sees a bare 401 —
 * which is exactly how a successful payment can leave no trace on the platform
 * (mkt_payment_events stays empty, so no membership activates and no credits
 * are granted).
 *
 * This is the same publishable key the browser bundle already ships. It
 * identifies the project and grants nothing by itself; the shared secret is
 * still what authorises the payload.
 */
export const SUPABASE_PUBLISHABLE_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImtpZCI6ImI0OGZmNzcyLWZkNWItNDI4OC1iNTM2LTg2NDNiYmI1MmNiZSJ9.eyJwcm9qZWN0SWQiOiJrbnZ2ZGxxbHptY3llYXN6ZHNoZSIsInJvbGUiOiJhbm9uIiwiaWF0IjoxNzg4OTA5ODI0LCJleHAiOjIxMDQyNjk4MjQsImlzcyI6ImZhbW91cy5kYXRhYmFzZXBhZCIsImF1ZCI6ImZhbW91cy5jbGllbnRzIn0.k0WK5bcZKY7Ogg5RMyBv0lNQYe4IzvcMtvPQnCff27Y';

/**
 * THE COMPLETE HEADER SET for the CRM webhook action, in entry order. All four
 * are required; only the shared secret is private.
 */
export interface WebhookHeader {
  key: string;
  value: string;
  secret?: boolean;
  why: string;
}

export const WEBHOOK_HEADERS: WebhookHeader[] = [
  {
    key: 'apikey',
    value: SUPABASE_PUBLISHABLE_KEY,
    why: 'Platform gateway token. Without it the delivery is refused with DP003_INVALID_TOKEN before GigGone ever sees it.',
  },
  {
    key: 'Authorization',
    value: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
    why: 'The same publishable key in Bearer form. Some gateway versions read this instead of apikey — send both.',
  },
  {
    key: 'x-webhook-secret',
    value: WEBHOOK_SECRET_ENV,
    secret: true,
    why: 'The private shared secret. This is what authorises the payment payload itself.',
  },
  {
    key: 'Content-Type',
    value: 'application/json',
    why: 'The body is JSON.',
  },
];


/** Public site origin used for the post-payment return URLs. */
export const SITE_ORIGIN = 'https://giggone.com';

/** Credits granted per plan. Mirrors PLAN_INCLUDED_LEADS in constants.ts. */
export const WEBHOOK_PLAN_CREDITS: Record<string, number> = {
  basic: 5,
  pro: 10,
  premium: 20,
};

/** Monthly charge in cents. Premium is 49900 per month, never the term total. */
export const WEBHOOK_PLAN_CENTS: Record<string, number> = {
  basic: 9900,
  pro: 19900,
  premium: 49900,
};

/** Event names the webhook treats as a successful first payment. */
export const SUCCESS_EVENT_TYPES = [
  'payment_success',
  'payment.succeeded',
  'order.completed',
  'purchase',
  'paid',
  'sale',
];

/** Event names that renew an existing membership (reset the monthly credits). */
export const RENEWAL_EVENT_TYPES = [
  'renewal',
  'subscription.renewed',
  'invoice.paid',
  'recurring_payment',
];

/** Event names that can NEVER grant a credit. */
export const NON_GRANTING_EVENT_TYPES = [
  'payment_failed',
  'payment.failed',
  'invoice.failed',
  'declined',
  'cancel',
  'cancelled',
  'refunded',
  'pending',
  'abandoned',
];

/** Fields the workflow must send. */
export const REQUIRED_WEBHOOK_FIELDS = [
  { field: 'event_id', note: 'CRM/provider transaction id. Makes the delivery idempotent.' },
  { field: 'type', note: 'Use payment_success for a first payment, renewal for a rebill.' },
  { field: 'email', note: 'Customer email. Must match the GigGone account email.' },
  { field: 'plan', note: 'basic | pro | premium (the product name also works).' },
  { field: 'amount', note: 'Amount paid. 9900 / 19900 / 49900 cents, or 99 / 199 / 499 dollars.' },
];

/** Fields that are stored for reconciliation but are not required. */
export const OPTIONAL_WEBHOOK_FIELDS = [
  { field: 'contact_id', note: 'CRM contact id — stored on the subscription.' },
  { field: 'payment_id', note: 'Stripe payment / charge id.' },
  { field: 'subscription_id', note: 'Stripe or CRM subscription id.' },
  { field: 'subscription_status', note: 'active | trialing | past_due …' },
  { field: 'currency', note: 'Defaults to USD.' },
  { field: 'status', note: 'succeeded — extra confirmation of the outcome.' },
  { field: 'period_start', note: 'Billing period start.' },
  { field: 'period_end', note: 'Billing period end.' },
  { field: 'timestamp', note: 'Event time from the provider.' },
];

/**
 * The exact JSON body to paste into the CRM webhook action. The {{ }} tokens
 * are GoHighLevel merge fields — paste them as-is and the CRM substitutes the
 * real values at send time.
 */
export const webhookPayloadTemplate = (plan: string) =>
  JSON.stringify(
    {
      event_id: '{{payment.transaction_id}}',
      type: 'payment_success',
      status: 'succeeded',
      email: '{{contact.email}}',
      contact_id: '{{contact.id}}',
      plan,
      amount: WEBHOOK_PLAN_CENTS[plan] ?? 9900,
      currency: 'USD',
      payment_id: '{{payment.id}}',
      subscription_id: '{{payment.subscription_id}}',
      subscription_status: 'active',
      period_start: '{{payment.period_start}}',
      period_end: '{{payment.period_end}}',
      timestamp: '{{payment.date}}',
    },
    null,
    2
  );

/** Post-payment destination. This is what stops the customer on the receipt. */
export const returnUrlFor = (plan: string, lang: 'en' | 'es' = 'en') =>
  `${SITE_ORIGIN}/${lang}/${lang === 'es' ? 'pago-completado' : 'payment-complete'}?plan=${plan}`;

/** Ordered, click-by-click CRM setup. Rendered by the admin panel. */
export interface SetupStep {
  title: string;
  clicks: string[];
}

export const CRM_SETUP_STEPS: SetupStep[] = [
  {
    title: '1. Confirm the live payment link and its price',
    clicks: [
      'GoHighLevel left sidebar > Payments > Payment Links.',
      'Open the Basic link. Check the price shows $99.00 / month and the product is the Basic membership.',
      'Click Preview to open the hosted page exactly as a customer sees it, then close it. Do not pay.',
      'Repeat for Pro ($199.00 / month) and Premium ($499.00 / month, 12 monthly payments).',
      'Copy each link URL. These are the values stored as GHL_BASIC_CHECKOUT_URL, GHL_PRO_CHECKOUT_URL and GHL_PREMIUM_CHECKOUT_URL.',
    ],
  },
  {
    title: '2. Set the post-payment redirect on each payment link',
    clicks: [
      'Still in Payments > Payment Links, click the Basic link > Edit.',
      'Find "After payment" / "Redirect URL" / "Thank-you page".',
      'Choose Custom URL and paste the Basic return URL shown above (English or Spanish product).',
      'Save. Repeat for Pro and Premium with their own return URLs.',
      'This is what replaces the generic GoHighLevel receipt page.',
    ],
  },
  {
    title: '3. Create the workflow that notifies GigGone',
    clicks: [
      'Left sidebar > Automation > Workflows > + Create Workflow > Start from Scratch.',
      'Name it exactly: GigGone — Activate membership after successful payment.',
      'Click "Add New Workflow Trigger".',
      'Choose the trigger "Payment Received" (also listed as "Order Submitted / Payment Success" in some accounts).',
      'Add filter: Payment Status equals Succeeded. If an Event Type filter exists, set it to Payment Success and Subscription Payment (renewals).',
      'Add filter: Product / Payment Link is one of Basic, Pro, Premium. Leave the $29 single lead OUT — GigGone takes that card itself.',
      'Save the trigger.',
    ],
  },
  {
    title: '4. Add the webhook action — ALL FOUR HEADERS',
    clicks: [
      'Click + > Webhook (under "Developer" / "External").',
      'Method: POST.',
      'URL: paste the production webhook URL shown above.',
      'Header 1: Key = apikey, Value = the publishable gateway token shown above (copy button provided).',
      'Header 2: Key = Authorization, Value = Bearer <the same publishable token> (copy button provided).',
      'Header 3: Key = x-webhook-secret, Value = the GHL_PAYMENT_WEBHOOK_SECRET value.',
      'Header 4: Key = Content-Type, Value = application/json.',
      'The two token headers are NOT optional. With only x-webhook-secret the hosting gateway answers 401 DP003_INVALID_TOKEN and GigGone never sees the payment — the card is charged and nothing activates.',
      'Body type: JSON / Raw. Paste the JSON template shown above for that plan.',
      'Confirm the plan field matches the product this branch handles (basic, pro or premium).',
      'Save the action, then Publish the workflow (top right toggle must read Publish, not Draft).',
    ],
  },
  {
    title: '5. Branch per plan (so the right number of credits is granted)',
    clicks: [
      'Easiest: build three workflows, one per plan, each filtered to one product and each sending its own plan value.',
      'Or: one workflow with an If/Else branch on the product, each branch holding its own webhook action.',
      'GigGone also falls back to the amount (9900 / 19900 / 49900) if the plan field is missing, but always send plan explicitly.',
    ],
  },
  {
    title: '6. Confirm GigGone answered 200',
    clicks: [
      'Open the workflow > top right "Execution Logs" (older accounts: History).',
      'Select the most recent run > expand the Webhook step.',
      'Response status must be 200 and the body must contain "success": true with a "result" value of activated, renewed or plan_changed.',
      '401 with "code": "DP003_INVALID_TOKEN" = the apikey / Authorization headers are missing. The delivery never reached GigGone, so there is no record of it to find. Add both headers and re-run.',
      '401 with "error": "unauthorized" = the delivery DID reach GigGone but the x-webhook-secret value does not match. Compare the fingerprint shown above with the value pasted in the CRM.',
      '"result": "pending:no_matching_account" = GigGone has no account with that email yet — use the recovery tool below once the account exists.',
      '"result": "ignored:unknown_plan" = the plan field and the amount were both unrecognisable.',
    ],
  },

  {
    title: '7. Test without taking another real payment',
    clicks: [
      'Fastest and safest: click "Send safe test event" in the panel below. It posts a synthetic event to the live endpoint with the real secret, proves a 200 response, and grants nothing because the email can never match an account.',
      'CRM-side test: in the workflow webhook action click "Test Webhook" — GoHighLevel sends sample data to the same URL and shows the response inline.',
      'End-to-end with no money: switch the GoHighLevel payment integration to Stripe TEST mode, pay the link with 4242 4242 4242 4242, then switch back to Live. Use 4000 0000 0000 9995 for an insufficient-funds decline and confirm no credit is granted.',
      'Never re-charge a real customer to test a webhook — replay the original transaction with the recovery tool instead.',
    ],
  },
];
